import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR, UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { _HttpClient } from '@delon/theme';
import { NzSelectComponent, NzSelectModeType } from 'ng-zorro-antd/select';

import { InlineAddPanelComponent } from '../../inline-add-panel/inline-add-panel.component';
import { StockLocationAddConfigService } from '../../inline-add-panel/stock-location-add-config.service';
import { SelectBaseComponent } from '../select-base';
import { SelectInstantBaseComponent } from '../selectInstant-base';
import { SelectInstantBaseNewComponent } from '../selectInstant-base-new';
import { UserDefaultService } from 'src/app/shared/services/user-default.service';

@Component({
  selector: 'app-stock-location-select',
  templateUrl: './stock-location-select.component.html',
  styles: [
    `
      /* ===== CONTAINER STYLES ===== */
      .stock-location-select-container {
        position: relative;
        width: 100%;
      }

      /* ===== ADD NEW BUTTON STYLES ===== */
      ::ng-deep .add-new-button {
        color: #000000 !important;
        font-weight: 500 !important;
        width: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 2px !important;
        background-color: transparent !important;
        border: none !important;
      }

      ::ng-deep .add-new-button .anticon {
        color: #000000 !important;
      }

      /* ===== ULTRA COMPACT DROPDOWN STYLES ===== */
      ::ng-deep .ant-select-dropdown:has(.stock-location-select-container) {
        max-height: none !important;
        overflow: hidden !important;
        width: 300px !important;
      }

      ::ng-deep
        .ant-select-dropdown:has(.stock-location-select-container)
        .ant-select-dropdown-menu {
        max-height: 200px !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
      }

      ::ng-deep
        .ant-select-dropdown:has(.stock-location-select-container)
        .ant-select-item-option {
        height: auto !important;
        min-height: 32px !important;
        padding: 4px 12px !important;
      }

      /* ===== DROPDOWN CONTAINER STYLES ===== */
      ::ng-deep
        .ant-select-dropdown:has(.stock-location-select-container)
        .dropdown-container {
        display: block !important;
        width: 100% !important;
      }

      /* ===== SPECIAL OPTION HIGHLIGHTING ===== */
      ::ng-deep .ant-select-item-option-content .special-option {
        text-align: center !important;
      }

      /* ===== YELLOW HIGHLIGHT FOR SECOND ITEM ===== */
      ::ng-deep
        .dropdownSelect:not(:has(.ant-select-item-option-active))
        .ant-select-item-option:nth-child(2):not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover),
      ::ng-deep
        .dropdownSelect
        .ant-select-item-option:first-child:last-child:not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StockLocationSelectComponent),
      multi: true
    }
  ]
})
export class StockLocationSelectComponent
  extends SelectInstantBaseNewComponent
  implements OnInit, AfterViewInit
{
  @ViewChild('selector') nzSelectComponent!: NzSelectComponent;

  @Input() tabindex: string | null = null;
  @Input() mode: NzSelectModeType = 'default';
  @Input() nzAllowClear: boolean = true;

  @Output() readonly addNewStockLocation = new EventEmitter<any>();

  stockLocationConfig: any;
  inlineDefaults: any = null;

  NotShowInReport: boolean = false;
  isLocationRestricted: boolean = false;

  constructor(
    http: _HttpClient,
    router: Router,
    cdr: ChangeDetectorRef,
    private resueTabService: ReuseTabService,
    private stockLocationAddConfigService: StockLocationAddConfigService,
    private userDefaultService: UserDefaultService
  ) {
    super(http, cdr, router);
  }

  ngOnInit(): void {
    this.checkAddNewOptionInReportAccess();
    this.stockLocationConfig =
      this.stockLocationAddConfigService.getStockLocationConfig();
    this.userDefaultService.isLocationRestricted().subscribe(isRestricted => {
      this.isLocationRestricted = isRestricted;
    });

    // Listen to search form changes to store instant data
    this.searchForm.controls.keyword.valueChanges.subscribe(value => {
      if (value) {
        const trimmedValue = value.trim();
        this.storedInstantData = trimmedValue;
        // Also store in pendingInstantData for Add New (handled by base class)
        this.pendingInstantData = trimmedValue;
      }
    });

    // Ensure the correct item is active whenever the list updates while open
    this.onListFilled.subscribe(() => {
      if ((this.nzSelectComponent as any)?.nzOpen) {
        this.ensureFirstItemActivatedAndFocused();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.tabindex) {
      const nzSelectElement =
        this.nzSelectComponent.originElement.nativeElement;
      const searchInput = nzSelectElement.querySelector('input');

      if (searchInput) {
        searchInput.setAttribute('tabindex', this.tabindex?.toString() || '0');

        // Listen to input events directly
        searchInput.addEventListener('input', (event: any) => {
          if (event.target.value) {
            const trimmedValue = event.target.value.trim();
            this.storedInstantData = trimmedValue;
            // Also store in pendingInstantData for Add New (handled by base class)
            this.pendingInstantData = trimmedValue;
          }
        });
      }
    }
  }

  getUrl(searchValue: string): string {
    let filter = `isActive eq true`;
    if (searchValue)
      return `api/stocklocations/lookup?$top=50&$skip=0&$orderby=code asc&$filter=${filter}&keyword=${encodeURIComponent(searchValue)}`;
    else
      return `api/stocklocations/lookup?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
  }

  // set the location want to navigate
  jump(): string {
    return '/stk/location/';
  }

  // set the param
  queryParam(): String {
    return 'stockLocationId';
  }

  checkAddNewOptionInReportAccess(): boolean {
    let curUrl = this.resueTabService.curUrl.toLowerCase();

    if (curUrl.includes('report')) {
      this.NotShowInReport = true;
    } else {
      this.NotShowInReport = false;
    }

    return this.NotShowInReport;
  }

  // Override the addNew method to show inline panel instead of navigation
  override addNew(): void {
    // Use pendingInstantData which was captured synchronously in base class onAddNewClick
    // This ensures we have the data even if storedInstantData was cleared
    const instantDataToUse = this.pendingInstantData || this.storedInstantData;

    // If we still don't have data, try to capture it
    if (!instantDataToUse) {
      this.captureCurrentSearchValue();
      const currentSearchValue = this.getCurrentSearchValue();
      if (currentSearchValue) {
        this.pendingInstantData = currentSearchValue;
      }
    }

    // Final fallback - use whatever we have
    const finalInstantData =
      this.pendingInstantData || this.storedInstantData || '';

    // Load options and defaults first, then show panel
    Promise.all([this.loadSelectOptions(), this.fetchInlineDefaults()]).then(
      () => {
        // Merge instant data into initialData as fallback
        if (finalInstantData) {
          this.inlineDefaults = {
            ...this.inlineDefaults,
            code: finalInstantData
          };
        }

        this.showInlineAddPanel = true;
        // Force change detection to ensure the panel is rendered
        this.cdr.detectChanges();

        // Retry mechanism to ensure changeInstantData is called after form is ready
        const trySetInstantData = (retries: number = 10) => {
          if (!finalInstantData) return;

          if (this.inlineAddPanel && this.inlineAddPanel.formGroup) {
            const instantFieldName =
              this.stockLocationConfig?.fields?.find(
                (f: any) => f.isInstantData
              )?.name || 'code';
            const control = this.inlineAddPanel.formGroup.get(instantFieldName);
            if (control) {
              this.inlineAddPanel.changeInstantData(finalInstantData);
              return;
            }
          }

          if (retries > 0) {
            setTimeout(() => trySetInstantData(retries - 1), 50);
          }
        };

        // Try immediately and with retries
        setTimeout(() => {
          if (this.inlineAddPanel && finalInstantData) {
            this.inlineAddPanel.changeInstantData(finalInstantData);
          }
          trySetInstantData();
        }, 0);

        if (this.nzSelectComponent) {
          this.nzSelectComponent.setOpenState(false);
        }
      }
    );
  }

  onStockLocationSaved(stockLocationData: any): void {
    // Create the stock location object in the expected format
    const stockLocation = {
      id: stockLocationData.id,
      code: stockLocationData.code,
      name: stockLocationData.name,
      isActive: stockLocationData.isActive
    };

    // Add the new stock location to the list and select it
    this.list.unshift(stockLocation);

    // Set the stock location object in the form control to trigger valueChanges
    this.value = stockLocation.id;
    this.selected = stockLocation.id;

    // Set the stock location object directly in the form control
    this.onChange(stockLocation);
    this.selectedChange.emit(stockLocation);
    this.showInlineAddPanel = false;
  }

  onStockLocationCreated(event: { item: any; selectionConfig?: any }): void {
    if (event.item && event.selectionConfig) {
      // Create the stock location object in the expected format
      const stockLocation = {
        id: event.item[event.selectionConfig.valueField],
        code: event.item.code,
        name: event.item[event.selectionConfig.labelField],
        isActive: event.item.isActive
      };

      // Add the new stock location to the list and select it
      this.list.unshift(stockLocation);

      // Close the inline panel first
      this.showInlineAddPanel = false;

      // Set the stock location object in the form control to trigger valueChanges
      this.value = stockLocation.id;
      this.selected = stockLocation.id;

      // Set the stock location object directly in the form control
      this.onChange(stockLocation);
      this.writeValue(stockLocation);
      // Emit addNewStockLocation event for parent component to handle
      this.addNewStockLocation.emit(stockLocation);

      // Clear stored instant data after successful creation
      this.clearStoredInstantData();

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  onStockLocationCancelled(): void {
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onStockLocationMoreDetails(): void {
    // Navigate to stock location list page with all the current form data
    const formData = this.inlineAddPanel?.formGroup?.value;
    if (formData) {
      // Create query params with all form fields
      const queryParams: any = {};

      // Add all form fields to query params
      if (formData.name) queryParams.StockLocationName = formData.name;
      if (formData.code) queryParams.StockLocationCode = formData.code;
      if (formData.isActive !== undefined)
        queryParams.IsActive = formData.isActive;

      this.router.navigate(['/stk/location'], {
        queryParams: queryParams
      });
    } else {
      this.router.navigate(['/stk/location']);
    }
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onFieldChange(event: { fieldName: string; value: any }): void {
    // Handle field changes - auto-code component will handle code generation
  }

  private loadSelectOptions(): Promise<void> {
    // For stock location, we don't need to load additional options
    return Promise.resolve();
  }

  private fetchInlineDefaults(): Promise<void> {
    // Call server to get defaults similar to stock location detail new()
    return new Promise(resolve => {
      this.http.get('api/stocklocations/new').subscribe({
        next: (data: any) => {
          const defaults: any = {};
          if (data?.isActive !== undefined) defaults.isActive = data.isActive;
          this.inlineDefaults = defaults;
          resolve();
        },
        error: () => {
          this.inlineDefaults = null;
          resolve();
        }
      });
    });
  }

}
